import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { describeDedupeSkips } from "@repo/adapter-core/crawl-policy";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  ExternalLink,
  Loader2,
  XCircle,
} from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Input } from "@repo/ui/input";
import { ScrollArea } from "@repo/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@repo/ui/tabs";
import { cn } from "@repo/ui/lib/utils";
import {
  fetchJobDiscoveryTasks,
  fetchJobRunRows,
  fetchJobRunSummary,
  type JobRunDetailQueueRow,
} from "@/lib/api";
import { useLoadMore } from "@/hooks/useLoadMore";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDate } from "@/lib/formatDate";
import { DiscoveryTasksPanel } from "@/components/jobs/DiscoveryTasksPanel";
import { ScrapedDataDialog } from "@/components/jobs/ScrapedDataDialog";

/**
 * Run-detail page: paginated audit trail for a single job run.
 *
 *   - Top-of-page counters come from `fetchJobRunSummary` head-counts —
 *     always the *true* totals for the run, never the loaded slice.
 *   - "Scrapes" tab loads scrape_queue rows 200 at a time via `useLoadMore`;
 *     "Load more" appears at the bottom when more rows exist on the server.
 *   - "Discovery" tab shows the same DiscoveryTasksPanel the JobDetailsDialog
 *     overview uses, scoped to this single run.
 *   - Clicking a queue row opens ScrapedDataDialog (structured data, metadata,
 *     files, snapshot tabs).
 *   - Back link returns to /dashboard/jobs/:jobId so the parent modal reopens
 *     at the History tab.
 */

const STATUS_TINT: Record<JobRunDetailQueueRow["status"], string> = {
  completed: "bg-emerald-500/5 hover:bg-emerald-500/10",
  failed: "bg-red-500/5 hover:bg-red-500/10",
  pending: "hover:bg-muted/30",
  in_progress: "bg-blue-500/5 hover:bg-blue-500/10",
};

const PAGE_SIZE = 200;

function StatusBadge({ row }: { row: JobRunDetailQueueRow }) {
  if (row.is_out_of_range) {
    return (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
        out of range
      </Badge>
    );
  }
  if (row.status === "completed") {
    return (
      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">
        <CheckCircle className="w-3 h-3 mr-1" />
        scraped
      </Badge>
    );
  }
  if (row.status === "failed") {
    return (
      <Badge variant="outline" className="bg-red-500/10 text-red-700 border-red-500/30">
        <XCircle className="w-3 h-3 mr-1" />
        failed
      </Badge>
    );
  }
  if (row.status === "in_progress") {
    return (
      <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/30">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
        in progress
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      <Clock className="w-3 h-3 mr-1" />
      pending
    </Badge>
  );
}

// Treat the literal strings "null"/"undefined"/"n/a" as missing — the
// extractor LLM emits these when a field is absent and they leaked through
// the priceDisplay branch as user-visible "null" in the Sold column.
const PRICE_PLACEHOLDER_STRINGS = new Set(["null", "undefined", "n/a", "na", "none", "-", "—"]);

function formatSalePrice(data: Record<string, unknown> | null): string {
  if (!data) return "—";
  const candidate = data.salePrice ?? data.sale_price ?? data.price;
  if (typeof candidate === "number" && candidate > 0) {
    return `$${Math.round(candidate / 1000).toLocaleString()}k`;
  }
  if (typeof data.priceDisplay === "string") {
    const trimmed = data.priceDisplay.trim();
    if (trimmed && !PRICE_PLACEHOLDER_STRINGS.has(trimmed.toLowerCase())) {
      return trimmed;
    }
  }
  return "—";
}

function formatSaleDate(data: Record<string, unknown> | null): string {
  if (!data) return "—";
  const candidate = (data.saleDate ?? data.sale_date) as string | undefined;
  if (!candidate) return "—";
  return formatDate(candidate);
}

const JobRunDetail = () => {
  const { jobId = "", runId = "" } = useParams<{ jobId: string; runId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"scrapes" | "discovery">("scrapes");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selectedRow, setSelectedRow] = useState<JobRunDetailQueueRow | null>(null);

  // True totals — independent of how many rows are loaded.
  const summaryQuery = useQuery({
    queryKey: ["job-run-summary", runId],
    queryFn: () => fetchJobRunSummary(runId),
    enabled: Boolean(runId),
    refetchInterval: 10_000, // refresh totals every 10s while the page is open
  });
  const counts = summaryQuery.data;
  const skippedTotal = counts
    ? Object.values(counts.skipped).reduce((sum, n) => sum + n, 0)
    : 0;

  // Discovery tasks — small enough to fetch all at once.
  const discoveryQuery = useQuery({
    queryKey: ["job-run-discovery", runId],
    queryFn: () => fetchJobDiscoveryTasks(runId),
    enabled: Boolean(runId),
  });

  // Paginated rows for the Scrapes tab. resetKey includes the search so a
  // new search starts from offset 0 and uses the server-side filter count.
  const rowsFetcher = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) =>
      fetchJobRunRows(runId, { offset, limit, search: debouncedSearch }),
    [runId, debouncedSearch],
  );

  const {
    items: rows,
    totalCount: filteredCount,
    isLoading: isLoadingRows,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useLoadMore<JobRunDetailQueueRow>({
    fetcher: rowsFetcher,
    pageSize: PAGE_SIZE,
    resetKey: `${runId}::${debouncedSearch}`,
    enabled: Boolean(runId),
  });

  // Group loaded rows by (state, suburb, adapter) for sticky-header display.
  // Grouping happens only on the rows we have — subsequent pages get
  // appended into the right group as they load.
  const groups = useMemo(() => {
    const map = new Map<string, { suburb_name: string; state: string; adapter_slug: string; rows: JobRunDetailQueueRow[] }>();
    for (const row of rows) {
      const key = `${row.state}::${row.suburb_name}::${row.adapter_slug}`;
      const existing = map.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        map.set(key, {
          suburb_name: row.suburb_name,
          state: row.state,
          adapter_slug: row.adapter_slug,
          rows: [row],
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.state !== b.state) return a.state.localeCompare(b.state);
      if (a.suburb_name !== b.suburb_name) return a.suburb_name.localeCompare(b.suburb_name);
      return a.adapter_slug.localeCompare(b.adapter_slug);
    });
  }, [rows]);

  // Back link: prefer history.back so the parent dialog state is preserved if
  // the user came from there; otherwise navigate to the modal-route directly.
  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/dashboard/jobs/${jobId}`);
    }
  }, [navigate, jobId]);

  // Close the data-modal on row navigation away.
  useEffect(() => {
    return () => setSelectedRow(null);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard/jobs">All jobs</Link>
            </Button>
            <span className="text-xs text-muted-foreground font-mono">
              run {runId.slice(0, 8)}
            </span>
            <span className="text-xs text-muted-foreground font-mono">
              · job {jobId.slice(0, 8)}
            </span>
          </div>
          <h1 className="text-2xl font-bold">Run detail</h1>
          <p className="text-sm text-muted-foreground">
            Every URL discovered for this run with its scrape outcome.
          </p>
        </div>
        {counts ? (
          <div className="flex items-center gap-3 text-sm">
            <span>
              <span className="font-mono">{counts.total.toLocaleString()}</span>{" "}
              queue rows
            </span>
            <span className="text-emerald-700">
              <span className="font-mono">{counts.completed.toLocaleString()}</span>{" "}
              scraped
            </span>
            {counts.failed > 0 && (
              <span className="text-red-700">
                <span className="font-mono">{counts.failed.toLocaleString()}</span>{" "}
                failed
              </span>
            )}
            {counts.outOfRange > 0 && (
              <span className="text-amber-700">
                <span className="font-mono">{counts.outOfRange.toLocaleString()}</span>{" "}
                out of range
              </span>
            )}
            {skippedTotal > 0 && (
              <span
                className="text-muted-foreground"
                title="Not fetched: the sale is already held completely; held but incomplete with 'Complete missing data' off; held incomplete and fetched by this adapter in the last 30 days; or another active run already holds the URL"
              >
                <span className="font-mono">{skippedTotal.toLocaleString()}</span> skipped (
                {describeDedupeSkips(counts.skipped)}
                )
              </span>
            )}
            {counts.pending > 0 && (
              <span className="text-muted-foreground">
                <span className="font-mono">{counts.pending.toLocaleString()}</span>{" "}
                pending
              </span>
            )}
          </div>
        ) : summaryQuery.isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : null}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as "scrapes" | "discovery")}>
        <TabsList>
          <TabsTrigger value="scrapes">Scrapes</TabsTrigger>
          <TabsTrigger value="discovery">
            Discovery
            {discoveryQuery.data && discoveryQuery.data.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs h-5 px-1.5">
                {discoveryQuery.data.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scrapes" className="space-y-3 mt-3">
          <div className="flex items-center justify-between gap-3">
            <Input
              placeholder="Search URL substrings…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 text-sm max-w-md"
            />
            {!isLoadingRows && (
              <span className="text-xs text-muted-foreground">
                {debouncedSearch ? (
                  <>
                    Showing {rows.length.toLocaleString()} of{" "}
                    {filteredCount.toLocaleString()} matches
                    {counts ? ` (run total ${counts.total.toLocaleString()})` : ""}
                  </>
                ) : (
                  <>
                    Showing {rows.length.toLocaleString()} of{" "}
                    {filteredCount.toLocaleString()}
                  </>
                )}
              </span>
            )}
          </div>

          {isLoadingRows ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : groups.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              {debouncedSearch ? "No matches for this search" : "No queue items for this run"}
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-340px)] border rounded-md">
              {groups.map((group) => (
                <div key={`${group.state}-${group.suburb_name}-${group.adapter_slug}`}>
                  <div className="sticky top-0 z-10 bg-background/95 backdrop-blur px-3 py-1.5 border-b text-xs font-medium flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] px-1">
                      {group.state}
                    </Badge>
                    <span>{group.suburb_name}</span>
                    <span className="text-muted-foreground">×</span>
                    <span className="font-mono text-muted-foreground">{group.adapter_slug}</span>
                    <span className="text-muted-foreground ml-auto">
                      {group.rows.length} URL{group.rows.length !== 1 ? "s" : ""} loaded
                    </span>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-32">Status</TableHead>
                        <TableHead>URL</TableHead>
                        <TableHead className="w-28 text-right">Sold</TableHead>
                        <TableHead className="w-24">Sold date</TableHead>
                        <TableHead className="w-32">Scraped at</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.rows.map((row) => (
                        <TableRow
                          key={row.id}
                          className={cn(
                            "cursor-pointer",
                            STATUS_TINT[row.status],
                            row.is_out_of_range && "bg-amber-500/5 hover:bg-amber-500/10",
                          )}
                          onClick={() => setSelectedRow(row)}
                        >
                          <TableCell>
                            <StatusBadge row={row} />
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-primary hover:underline break-all"
                              title={row.url}
                            >
                              {row.url.length > 80 ? `${row.url.slice(0, 80)}…` : row.url}
                              <ExternalLink className="w-3 h-3 shrink-0" />
                            </a>
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">
                            {formatSalePrice(row.scraped_data)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {formatSaleDate(row.scraped_data)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {row.scraped_at ? formatDate(row.scraped_at, { includeTime: true }) : "—"}
                          </TableCell>
                          <TableCell
                            className="text-xs text-red-600 max-w-[300px] truncate"
                            title={row.last_error ?? ""}
                          >
                            {row.last_error}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
              {hasMore && (
                <div className="p-3 border-t bg-background sticky bottom-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => void loadMore()}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading…
                      </>
                    ) : (
                      <>
                        Load more (+
                        {Math.min(PAGE_SIZE, filteredCount - rows.length).toLocaleString()})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </ScrollArea>
          )}
        </TabsContent>

        <TabsContent value="discovery" className="mt-3">
          {discoveryQuery.isLoading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <DiscoveryTasksPanel
              tasks={discoveryQuery.data ?? []}
              title="Discovery tasks (this run)"
            />
          )}
        </TabsContent>
      </Tabs>

      <ScrapedDataDialog
        row={selectedRow}
        open={selectedRow !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedRow(null);
        }}
      />
    </div>
  );
};

export default JobRunDetail;
